import { defineStore } from 'pinia';
import api from '@/services/axios';
import { useAuthStore } from '@/stores/auth';
import { LEGAL_DOCUMENT_KEYS, LEGAL_DOCUMENTS, acceptancePayload, currentVersion } from '@/content/legal';

const DISMISSED_KEY = 'legal-banner-dismissed';

/**
 * Which legal documents the signed-in user still has to accept.
 *
 * It reads `legal_acceptances` off the user the auth store already fetched, so
 * it costs no extra request. That key is also the feature switch: while the
 * server has no idea about acceptances the key is absent, `supported` is false
 * and nothing is shown or blocked. So this can ship to staging before the
 * backend half and simply do nothing, rather than locking everyone out of the
 * AI features with no way to accept.
 *
 * An empty object (the server supports it, the user has accepted nothing) is a
 * very different thing from a missing key, which is why the check below is on
 * the key and not on truthiness.
 */
export const useLegalStore = defineStore('legal', {
    state: () => ({
        dismissed: sessionStorage.getItem(DISMISSED_KEY) === '1',
        submitting: false,
    }),

    getters: {
        /** Does this server record acceptances at all? */
        supported() {
            const user = useAuthStore().userinfo;

            return !!user && Object.prototype.hasOwnProperty.call(user, 'legal_acceptances');
        },

        /** What the user has accepted: { privacy: '2026-10-26', terms: null }. */
        acceptances() {
            return useAuthStore().userinfo?.legal_acceptances ?? {};
        },

        /** The documents whose current version this user has not accepted. */
        pending() {
            if (!this.supported) return [];

            return LEGAL_DOCUMENT_KEYS.filter(
                (key) => this.acceptances[key] !== currentVersion(key),
            );
        },

        pendingDocuments() {
            return this.pending.map((key) => LEGAL_DOCUMENTS[key]);
        },

        needsAcceptance() {
            return useAuthStore().isLoggedIn && this.pending.length > 0;
        },

        /**
         * The one consequence of not having accepted: the AI features pause.
         * Nothing else in the app is withheld, which is the decision Tan took
         * on 2026-10-25.
         */
        aiBlocked() {
            return this.needsAcceptance;
        },
    },

    actions: {
        async accept() {
            this.submitting = true;

            try {
                await api.post('/legal/accept', acceptancePayload());

                // Re-read the user so the banner and the AI gates clear from
                // what the server actually stored, never from a local guess.
                await useAuthStore().fetchUser();

                this.undismiss();

                return true;
            } finally {
                this.submitting = false;
            }
        },

        /** Hide the banner for this browser session, without accepting. */
        dismiss() {
            this.dismissed = true;
            sessionStorage.setItem(DISMISSED_KEY, '1');
        },

        /** Bring it back, which is what a blocked AI click does. */
        undismiss() {
            this.dismissed = false;
            sessionStorage.removeItem(DISMISSED_KEY);
        },
    },
});
